import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitToken } from '@homeplay/app';
import { apiGetUpcomingLotteries } from '@homeplay/api';
import { useCallOnceWhen } from '../../hooks/useCallOnceWhen';
import { useLotteryFilters } from '../../hooks/useLotteryFilters';
import { usePagination } from '../../hooks/usePagination';
import { TAB_VALUE, LOTTERY_TYPE } from '../../constants';
import LotteryCard from '../LotteryCard';
import LotteryCardSkeleton from '../LotteryCard/LotteryCardSkeleton';
import CountrySelect from '../CountrySelect';
import WidgetError from '../WidgetError';
import RefreshButton from '../RefreshButton';
import classes from './LotteryList.module.scss';

const PAGE_SIZE = 9;

const TABS = [
  { value: TAB_VALUE.POPULAR, label: 'Popular' },
  { value: LOTTERY_TYPE.REGULAR, label: 'Regular' },
  { value: LOTTERY_TYPE.INSTANT, label: 'Rapid' },
];

const getTabLabel = (value) =>
  TABS.find((t) => t.value === value)?.label ?? value;

const LotteryList = ({
  intentArgs,
  message,
  onLotterySelect,
  onOutcomeSelect,
  onLotteriesLoaded,
  onWidgetDataLoaded,
}) => {
  const {
    country,
    lottery_name: lotteryName,
    format,
    with_bonus_ball: onlyWithBonusBall,
    limit,
    with_suggested_options: withSuggestedOptions = false,
  } = intentArgs ?? {};

  const unitToken = useSelector(selectUnitToken);
  const prefetchedData = intentArgs?.data;
  const [ignorePrefetchedData, setIgnorePrefetchedData] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    country && !country.includes(',') ? country : null
  );

  const [data, setData] = useState(prefetchedData ?? null);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!unitToken) return;
    setIsFetching(true);
    setError(null);
    try {
      const result = await apiGetUpcomingLotteries({
        unitToken,
        country: selectedCountry ?? undefined,
        lotteryName,
        format,
        onlyWithBonusBall,
        limit,
        withSuggestedOptions,
      });
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setIsFetching(false);
    }
  }, [
    unitToken,
    selectedCountry,
    lotteryName,
    format,
    onlyWithBonusBall,
    limit,
    withSuggestedOptions,
  ]);

  useEffect(() => {
    if (prefetchedData && !ignorePrefetchedData) return;
    fetchData();
  }, [fetchData, prefetchedData, ignorePrefetchedData]);

  const isDataLoaded = !!data || !!error;
  useCallOnceWhen(isDataLoaded, onWidgetDataLoaded);

  useEffect(() => {
    if (!data?.data || !onLotteriesLoaded) return;
    const shouldInclude = !!(country || lotteryName);
    onLotteriesLoaded(
      shouldInclude
        ? data.data.map((l) => ({ id: l.lottery_id, name: l.short_name }))
        : []
    );
  }, [data, onLotteriesLoaded, country, lotteryName]);

  const lotteries = data?.data ?? [];
  const totalLotteries = data?.count ?? 0;
  const displayMessage = data?.fallback_message || message;
  const availableCountries = data?.available_countries ?? [];

  useEffect(() => {
    if (
      selectedCountry &&
      availableCountries.length > 0 &&
      !availableCountries.includes(selectedCountry)
    ) {
      setSelectedCountry(null);
    }
  }, [availableCountries, selectedCountry]);

  const { activeTab, setActiveTab, tabCounts, filteredLotteries } =
    useLotteryFilters({
      lotteries,
      initialTab: TAB_VALUE.POPULAR,
      selectedCountry,
    });

  const { showAll, hasMore, displayedItems, toggleShowAll, resetPagination } =
    usePagination({
      items: filteredLotteries,
      initialDisplayCount: PAGE_SIZE,
    });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    resetPagination();
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value || null);
    setIgnorePrefetchedData(true);
    resetPagination();
  };

  const handleRefresh = () => {
    if (prefetchedData && !ignorePrefetchedData) {
      setIgnorePrefetchedData(true);
    } else {
      fetchData();
    }
  };

  if (!prefetchedData && error) {
    return <WidgetError onRetry={fetchData} error={error} />;
  }

  return (
    <div className={classes.Container}>
      <div className={classes.Header}>
        {displayMessage && (
          <p className={classes.HeaderText}>{displayMessage}</p>
        )}

        {totalLotteries > 0 && (
          <>
            <CountrySelect
              countries={availableCountries}
              value={selectedCountry}
              onChange={handleCountryChange}
              className={classes.CountryFilter}
            />

            <div className={classes.TabRow}>
              {TABS.map((tab) => {
                const isActive = activeTab === tab.value;
                const isDisabled =
                  !tabCounts[tab.value] && !(isFetching && isActive);
                return (
                  <button
                    key={tab.value}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => handleTabChange(tab.value)}
                    className={
                      classes.TabButton +
                      (isActive
                        ? ' ' + classes.TabActive
                        : ' ' + classes.TabInactive) +
                      (isDisabled ? ' ' + classes.TabDisabled : '')
                    }
                  >
                    {tab.label}
                  </button>
                );
              })}
              <RefreshButton
                onClick={handleRefresh}
                isFetching={isFetching}
                ariaLabel="Refresh lotteries"
                customClass={classes.RefreshButton}
              />
            </div>

            <p className={classes.HeaderSubtitle}>
              Click on a lottery to place your bet ({filteredLotteries.length}{' '}
              {filteredLotteries.length === 1 ? 'lottery' : 'lotteries'}{' '}
              available)
            </p>
          </>
        )}
      </div>

      <div className={classes.Grid}>
        {isFetching ? (
          Array.from({ length: 3 }, (_, i) => <LotteryCardSkeleton key={i} />)
        ) : totalLotteries === 0 ? (
          <p className={classes.EmptyText}>No lotteries found</p>
        ) : filteredLotteries.length === 0 ? (
          <p className={classes.EmptyText}>
            No {getTabLabel(activeTab)} lotteries available at the moment
          </p>
        ) : (
          displayedItems.map((lottery) => (
            <LotteryCard
              key={lottery.lottery_id}
              lottery={lottery}
              onSelect={onLotterySelect}
              onOutcomeSelect={onOutcomeSelect}
            />
          ))
        )}
      </div>

      {hasMore && (
        <div className={classes.LoadMoreWrapper}>
          <button
            type="button"
            onClick={toggleShowAll}
            className={classes.LoadMoreButton}
          >
            {showAll
              ? 'Show Less'
              : `Show All (${filteredLotteries.length - PAGE_SIZE} more)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default LotteryList;
